import type { Styles } from '@adornis/ass/style.js';
import { loginWithSAML } from '@adornis/authentication/saml-client/client/loginWithSAML.js';
import type { BaseQLFieldDefinition } from '@adornis/baseql/metadata/types.js';
import '@adornis/bh-matching-config/components/buddy-page-admin-legal-pages.js';
import '@adornis/bh-matching-config/components/buddy-page-data-privacy.js';
import '@adornis/bh-matching-config/components/buddy-page-imprint.js';
import { configRegistry } from '@adornis/bh-matching-config/entities/global-config.js';
import '@adornis/bh-matching-export/components/buddy-page-export.js';
import '@adornis/bh-matching-mails/components/buddy-page-mails.js';
import '@adornis/bh-matching-matches/components/buddy-page-manage-matches.js';
import '@adornis/bh-matching-matches/components/buddy-page-matching.js';
import '@adornis/bh-matching-signup/components/buddy-page-signup.js';
import '@adornis/bh-matching-signup/components/buddy-page-success-register.js';
import '@adornis/bh-matching-timeframes/components/matching-page-timeframes.js';
import '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import '@adornis/bh-matching-user/components/buddy-page-admin-users.js';
import {
  BuddyUserMentee,
  BuddyUserMentor,
  DefaultMenteeUser,
  DefaultMentorUser,
} from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { userFieldBlackList } from '@adornis/bh-matching-user/user-field-blacklist.js';
import '@adornis/buchungsheld-components/authentication/bh-login.js';
import { config } from '@adornis/buchungsheld-db/config.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-loader.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-section.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import type { GenericFieldFormController } from '@adornis/cms/client/generic-field-formcontroller.js';
import '@adornis/fonts/fonts.js';
import { makeRouter, type RouteConfig, type RouterObject } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { routingState } from '@adornis/router/client/routing-state.js';
import { clientTranslator } from '@adornis/translation-core/client/clientTranslator.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { Language } from '@adornis/translation-core/languages.js';
import '@adornis/translation/client/translation-editor.js';
import { authenticationGuard } from '@adornis/user-forms/authentication-guard.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import '@adornis/user-forms/x-login.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { watch } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import logo from './assets/buchungsheld_logo_small.png';

let adminRouter: RouterObject<any>;

export type CustomFieldMapping = (
  field: BaseQLFieldDefinition,
  form: GenericFieldFormController<BuddyUserMentee | BuddyUserMentor>,
) => Renderable | undefined;

type ConfigOverrideKeys =
  | 'root'
  | 'signup-mentor'
  | 'signup-mentee'
  | 'signup-success'
  | 'matching'
  | 'matches'
  | 'fields-mentor'
  | 'fields-mentee'
  | 'timeframes'
  | 'admin-users'
  | 'mails'
  | 'translation-editor'
  | 'legal-pages'
  | 'export'
  | 'data-privacy'
  | 'imprint'
  | 'admin-login';

export const initAdminRouter = ({
  configOverrides,
  fieldsOverride,
  mentorCustomFieldMapping,
  menteeCustomFieldMapping,
  mentorUserClass = DefaultMentorUser,
  mentorFieldBlackList = userFieldBlackList,
  menteeUserClass = DefaultMenteeUser,
  menteeFieldBlackList = userFieldBlackList,
}: {
  configOverrides?: Partial<Record<ConfigOverrideKeys, Pick<RouteConfig<any>, 'render'>>>;
  fieldsOverride?: (userClass: typeof BuddyUserMentor | typeof BuddyUserMentee) => BaseQLFieldDefinition[];
  mentorCustomFieldMapping?: CustomFieldMapping;
  menteeCustomFieldMapping?: CustomFieldMapping;
  mentorUserClass?: typeof BuddyUserMentor;
  mentorFieldBlackList?: string[];
  menteeUserClass?: typeof BuddyUserMentee;
  menteeFieldBlackList?: string[];
}) => {
  configRegistry.fieldsOverride ??= fieldsOverride;
  configRegistry.mentorCustomFieldMapping ??= mentorCustomFieldMapping;
  configRegistry.menteeCustomFieldMapping ??= menteeCustomFieldMapping;
  if (!adminRouter)
    adminRouter = makeRouter(
      {
        root: {
          path: '/',
          render: params => {
            if (configOverrides?.root) return configOverrides.root.render?.(params);
            return html`
              <x-flex space="md" padding="md">
                <x-h1> Homepage </x-h1>
              </x-flex>
            `;
          },
          enter: () => {
            requestAnimationFrame(() => adminRouter.goTo('matching'));
          },
          metaData: { visible: false },
        },
        'admin-login': {
          path: '/admin-login',
          render: params => {
            if (configOverrides?.['admin-login']) return configOverrides?.['admin-login']?.render?.(params);
            return nothing;
          }, //only used in Fulda and overriden in its respective main-element.ts
          metaData: { visible: false },
        },
        'signup-mentor': {
          path: '/signup-mentor',
          render: params => {
            if (configOverrides?.['signup-mentor']) return configOverrides?.['signup-mentor'].render?.(params);
            return html`
              <buddy-page-signup
                .userClass=${mentorUserClass}
                page-title=${clientTranslator.translate('buddy', 'mentorSignupTitle')}
                .fieldsOverride=${fieldsOverride}
                .customFieldMapping=${mentorCustomFieldMapping}
              ></buddy-page-signup>
            `;
          },
          metaData: { icon: 'description' },
        },
        'signup-mentee': {
          path: '/signup-mentee',
          render: params => {
            if (configOverrides?.['signup-mentee']) return configOverrides?.['signup-mentee'].render?.(params);
            return html`
              <buddy-page-signup
                .userClass=${menteeUserClass}
                page-title=${clientTranslator.translate('buddy', 'menteeSignupTitle')}
                .fieldsOverride=${fieldsOverride}
                .customFieldMapping=${menteeCustomFieldMapping}
              ></buddy-page-signup>
            `;
          },
          metaData: { icon: 'description' },
        },
        'signup-success': {
          path: '/signup-success',
          render: params => {
            if (configOverrides?.['signup-success']) return configOverrides?.['signup-success'].render?.(params);
            return html` <buddy-page-success-register page-title=${''} flex></buddy-page-success-register> `;
          },
          metaData: { icon: 'check', visible: false },
        },
        matching: {
          path: '/matching',
          render: params => {
            if (configOverrides?.['matching']) return adminPage(configOverrides?.['matching'].render?.(params), true);
            return adminPage(
              html`
                <buddy-page-matching
                  .menteeUserClass=${menteeUserClass}
                  .mentorUserClass=${mentorUserClass}
                ></buddy-page-matching>
              `,
              true,
            );
          },
          metaData: { icon: 'join_inner' },
        },
        matches: {
          path: '/matches',
          render: params => {
            if (configOverrides?.['matches']) return adminPage(configOverrides?.['matches'].render?.(params));
            return adminPage(html` <buddy-page-manage-matches></buddy-page-manage-matches> `);
          },
          metaData: { icon: 'manage_accounts' },
        },
        'fields-mentor': {
          path: '/fields-mentor',
          render: params => {
            if (configOverrides?.['fields-mentor'])
              return adminPage(configOverrides?.['fields-mentor'].render?.(params));
            return adminPage(
              html`
                <tr-entity-fields
                  .userClass=${mentorUserClass}
                  page-title="Verfügbare Felder für die Mentoren-Registrierung"
                  .fieldBlackList=${mentorFieldBlackList}
                ></tr-entity-fields>
              `,
            );
          },
          metaData: { icon: 'edit_square', visible: false },
        },
        'fields-mentee': {
          path: '/fields-mentee',
          render: params => {
            if (configOverrides?.['fields-mentee'])
              return adminPage(configOverrides?.['fields-mentee'].render?.(params));
            return adminPage(html`
              <tr-entity-fields
                .userClass=${menteeUserClass}
                page-title="Verfügbare Felder für die Mentee-Registrierung"
                .fieldBlackList=${menteeFieldBlackList}
              ></tr-entity-fields>
            `);
          },
          metaData: { icon: 'edit_square', visible: false },
        },
        timeframes: {
          path: '/timeframes',
          render: params => {
            if (configOverrides?.['timeframes']) return adminPage(configOverrides?.['timeframes'].render?.(params));

            return adminPage(html` <matching-page-timeframes institution-id="global"></matching-page-timeframes> `);
          },
          metaData: { icon: 'calendar_month' },
        },
        'admin-users': {
          path: '/admin-users',
          render: params => {
            if (configOverrides?.['admin-users']) return adminPage(configOverrides?.['admin-users'].render?.(params));
            return adminPage(html` <buddy-page-admin-users></buddy-page-admin-users> `);
          },
          metaData: { icon: 'person' },
        },
        mails: {
          path: '/mails',
          render: params => {
            if (configOverrides?.['mails']) return adminPage(configOverrides?.['mails'].render?.(params));
            return adminPage(html` <buddy-page-mails .userClass=${BuddyUserMentee}></buddy-page-mails> `);
          },
          metaData: { icon: 'mail' },
        },
        'translation-editor': {
          path: '/translation-editor',
          render: params => {
            if (configOverrides?.['translation-editor'])
              return adminPage(configOverrides?.['translation-editor'].render?.(params));
            return html`
              <x-flex padding="md" h100 ${css({ overflow: 'auto' })}>
                <translation-editor></translation-editor>
              </x-flex>
            `;
          },
          metaData: { icon: 'language_chinese_dayi' },
        },
        export: {
          path: '/export',
          render: params => {
            if (configOverrides?.['export']) return adminPage(configOverrides?.['export'].render?.(params));
            return adminPage(html` <buddy-page-export></buddy-page-export> `);
          },
          metaData: { icon: 'upgrade' },
        },
        'legal-pages': {
          path: '/legal-pages',
          render: params => {
            if (configOverrides?.['legal-pages']) return adminPage(configOverrides?.['legal-pages'].render?.(params));
            return adminPage(html` <buddy-page-admin-legal-pages></buddy-page-admin-legal-pages> `);
          },
          metaData: { icon: 'copyright' },
        },
        imprint: {
          path: '/imprint',
          render: params => {
            if (configOverrides?.['imprint']) return configOverrides?.['imprint'].render?.(params);
            return html` <buddy-page-imprint></buddy-page-imprint> `;
          },
          metaData: { visible: false },
        },
        'data-privacy': {
          path: '/data-privacy',
          render: params => {
            if (configOverrides?.['data-privacy']) return configOverrides?.['data-privacy'].render?.(params);
            return html` <buddy-page-data-privacy></buddy-page-data-privacy> `;
          },
          metaData: { visible: false },
        },
      },
      {
        fallback: () => {
          if (configOverrides?.['fallback']) return configOverrides?.['fallback'].render?.(params);
          return html` 404 page not found `;
        },
      },
    );

  return adminRouter;
};

function adminPage(content: Renderable, fullWidth?: boolean) {
  const guard = permissionGuard(
    MatchingPermission.VIEW_ADMIN_DASHBOARD,
    () => html`
      <x-flex flex ${css({ overflowY: 'auto' })}>
        ${fullWidth ? content : html` <x-section w100> ${content} </x-section> `}
      </x-flex>
    `,
    {
      forbiddenTemplate: notAllowedPage(),
      context: GLOBAL_CONTEXT,
    },
  );

  const samlImplementation = config.get('SAML_IMPLEMENTATION');

  const errorMessage = routingState.searchParams.getOnce('error_message');

  return html`
    <x-flex flex center>
      ${authenticationGuard(
        () => html` ${guard} `,

        html`<x-flex h100 w100 center crossaxis-center flex>
          ${choose(
            samlImplementation,
            [
              [
                'dfn-aai', //right now hs-fulda is the only matching/buddy/mentoring deployment using SAML
                () =>
                  html`
                    ${errorMessage ? html` <x-text tone="error">${errorMessage}</x-text> ` : nothing}
                    <div ${css({ height: '1rem' })}></div>
                    <x-button icon="login" @click=${() => loginWithSAML()}> Login mit FD-Kennung </x-button>
                  `,
              ],
              [
                'hs-fulda', //right now hs-fulda is the only matching/buddy/mentoring deployment using SAML
                () =>
                  html`
                    ${errorMessage ? html` <x-text tone="error">${errorMessage}</x-text> ` : nothing}
                    <div ${css({ height: '1rem' })}></div>
                    <x-button icon="login" @click=${() => loginWithSAML()}> Login mit FD-Kennung </x-button>
                  `,
              ],
              [
                'adornis-authentik', //the only matching deployment using SAML is the dev one using adornis-authentik
                () => html`
                  <x-login placeholder-username="E-Mail" placeholder-password="Passwort"></x-login>
                  <x-flex ${css({ flexGrow: '0.25' })}></x-flex>
                  <x-button icon="login" @click=${() => loginWithSAML()}>Login mit FD-Kennung</x-button>
                `,
              ],
            ],
            //right now, this is the default case for all deployments other than fulda
            () => html`<x-login placeholder-username="E-Mail" placeholder-password="Passwort"></x-login>`,
          )}
        </x-flex> `,
      )}
    </x-flex>
  `;
}

function notAllowedPage() {
  return html`
    <x-empty-placeholder .img=${'close'} .text=${'Sie haben hierfür keine Berechtigung.'}></x-empty-placeholder>
  `;
}

/*
 * @element matching-page-admin
 */
@customElement('matching-page-admin')
export class MatchingPageAdmin extends ChemistryLitElement {
  /** non-reactive config, only able to set this statically */
  routerConfig?: Record<string, { render: () => Renderable }>;

  @property() logo?: string;

  @state() protected showSidebar = true;
  @state() protected readonly _language = new RXController(this, globalLanguageSubject);
  @state() protected readonly _currUser = new RXController(this, currentUser());

  override render() {
    if (this._currUser.isLoading || this._language.isLoading)
      return html` <x-flex h100 center crossaxis-center> <tr-loader></tr-loader> </x-flex> `;
    return html`
      <x-flex h100 horizontal> ${this._sidebar()} <x-flex flex container> ${this._content()} </x-flex> </x-flex>
    `;
  }

  protected _sidebar() {
    if (!this._currUser.value) return nothing;
    if (!this.showSidebar) return nothing;
    return this._currUser.value
      ? html`
          <tr-sidebar
            .isNarrow=${this.showSidebar}
            ${css({
              minWidth: this.sizes.sidebarMinWidth,
              background: this.colors.tone.snowWhite,
              boxShadow: `0 0 16px -8px`,
            })}
            .routes=${Object.entries(adminRouter.routes).map(([routeTitle, routeOpts]) => ({
              name: routeTitle,
              ...routeOpts,
            }))}
          >
            <img src=${this.logo ?? logo} slot="logo" ${css({ width: '30%', margin: 'auto' })} />
            <x-flex padding="md"> <matching-semester-selection></matching-semester-selection> </x-flex>
            <x-flex slot="bottom" horizontal space="sm">
              <tr-button-small
                icon="logout"
                mode="outline"
                .color=${this.colors.tone.black}
                .action=${() => logout().then(() => goTo('/'))}
              >
                <tr-tooltip> Ausloggen </tr-tooltip>
              </tr-button-small>
              <tr-button-small
                .color=${this.colors.tone.black}
                mode="outline"
                .action=${() => {
                  if (this._language.value === Language.GERMAN) {
                    void clientTranslator.selectLanguage(Language.ENGLISH);
                  } else {
                    void clientTranslator.selectLanguage(Language.GERMAN);
                  }

                  // this is need because else not every translation changes (i.e. sidebar items)
                  window.location.reload();
                }}
              >
                <tr-text>${this._language.value.toUpperCase()}</tr-text>
                <tr-tooltip .text=${clientTranslator.translate('change_language')}></tr-tooltip>
              </tr-button-small>
            </x-flex>
          </tr-sidebar>
        `
      : nothing;
  }

  protected _content() {
    return html` ${watch(adminRouter.outlet)} `;
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        '[container]': {
          overflow: 'auto',
          paddingBottom: this.spacing.md,
        },
      },
    ];
  }
}
